/* Sound is synthesised with Web Audio, so there are no files to load. Interaction sounds are mechanical
   (a snap for a pin, a click for buttons, a latch for lock in); the big moments are soft tones through one
   low-passed bus. Ported from Pinpoint v3. */
import { load, save } from './storage'

let enabled = load<boolean>('sound', true)
let ctx: AudioContext | null = null
let busNode: GainNode | null = null
let noiseBuf: AudioBuffer | null = null

export function soundOn() {
  return enabled
}
export function setSound(on: boolean) {
  enabled = on
  save('sound', on)
  if (on) sfx('click')
}

function ac(): AudioContext | null {
  if (!ctx) {
    try {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      ctx = new AC()
    } catch {
      return null
    }
  }
  if (ctx.state === 'suspended') void ctx.resume().catch(() => {})
  return ctx
}
export function audioContext() {
  return ac()
}

function bus(): GainNode | null {
  const a = ac()
  if (!a) return null
  if (!busNode) {
    const g = a.createGain()
    g.gain.value = 0.9
    const lp = a.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 4200
    lp.Q.value = 0.6
    g.connect(lp).connect(a.destination)
    busNode = g
  }
  return busNode
}

interface NoteOpts { f?: number; to?: number | null; t?: number; v?: number; delay?: number; attack?: number; shine?: number; type?: OscillatorType }
function note({ f = 440, to = null, t = 0.5, v = 0.16, delay = 0, attack = 0.02, shine = 0.28, type = 'sine' }: NoteOpts) {
  const a = ac(), b = bus()
  if (!a || !b || !enabled) return
  const t0 = a.currentTime + delay
  const g = a.createGain()
  g.gain.setValueAtTime(0.0001, t0)
  g.gain.linearRampToValueAtTime(v, t0 + attack)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + t)
  g.connect(b)
  const mk = (freq: number, mul: number, ty: OscillatorType) => {
    const o = a.createOscillator()
    o.type = ty
    o.frequency.setValueAtTime(freq, t0)
    if (to) o.frequency.exponentialRampToValueAtTime(Math.max(20, to * mul), t0 + t * 0.9)
    const gg = a.createGain()
    gg.gain.value = mul === 1 ? 1 : shine
    o.connect(gg).connect(g)
    o.start(t0)
    o.stop(t0 + t + 0.06)
  }
  mk(f, 1, type)
  if (shine > 0) mk(f * 2, 2, 'triangle')
}

interface SnapOpts { f?: number; q?: number; t?: number; v?: number; delay?: number; thump?: number }
function snap({ f = 2600, q = 1.1, t = 0.028, v = 0.5, delay = 0, thump = 0 }: SnapOpts = {}) {
  const a = ac(), b = bus()
  if (!a || !b || !enabled) return
  if (!noiseBuf) {
    noiseBuf = a.createBuffer(1, Math.floor(a.sampleRate * 0.06), a.sampleRate)
    const d = noiseBuf.getChannelData(0)
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1
  }
  const t0 = a.currentTime + delay
  const src = a.createBufferSource()
  src.buffer = noiseBuf
  const bp = a.createBiquadFilter()
  bp.type = 'bandpass'
  bp.frequency.value = f
  bp.Q.value = q
  const g = a.createGain()
  g.gain.setValueAtTime(0.0001, t0)
  g.gain.linearRampToValueAtTime(v, t0 + 0.003)
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + t)
  src.connect(bp).connect(g).connect(b)
  src.start(t0)
  src.stop(t0 + t + 0.02)
  if (thump > 0) {
    const o = a.createOscillator()
    o.type = 'sine'
    o.frequency.setValueAtTime(170, t0)
    o.frequency.exponentialRampToValueAtTime(70, t0 + 0.05)
    const gg = a.createGain()
    gg.gain.setValueAtTime(0.0001, t0)
    gg.gain.linearRampToValueAtTime(thump, t0 + 0.004)
    gg.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.06)
    o.connect(gg).connect(b)
    o.start(t0)
    o.stop(t0 + 0.08)
  }
}

function chord(freqs: number[], { t = 0.9, v = 0.13, delay = 0, attack = 0.05 } = {}) {
  freqs.forEach((f, i) => note({ f, t: t - i * 0.04, v: v * (1 - i * 0.12), delay: delay + i * 0.015, attack, shine: 0.2 }))
}

const SFX = {
  pin: () => snap({ f: 1500, q: 0.9, t: 0.045, v: 0.55, thump: 0.22 }),
  click: () => snap({ f: 3000, q: 1.4, t: 0.022, v: 0.32 }),
  tick: () => snap({ f: 3400, q: 1.6, t: 0.016, v: 0.18 }),
  lock: () => {
    snap({ f: 2200, q: 1.2, t: 0.03, v: 0.45 })
    snap({ f: 1200, q: 0.9, t: 0.05, v: 0.5, delay: 0.07, thump: 0.25 })
  },
  reveal: () => {
    note({ f: 330, to: 247, t: 0.85, v: 0.1, attack: 0.09, shine: 0.12 })
    note({ f: 494, to: 370, t: 0.7, v: 0.07, delay: 0.05, attack: 0.12, shine: 0.1 })
  },
  // points counting up: a quick run of soft ticks, slowing as it lands
  count: (ms?: number) => {
    const dur = Math.max(0.4, Math.min(1.4, (ms || 900) / 1000))
    for (let t = 0, gap = 0.035; t < dur; t += gap, gap *= 1.07) snap({ f: 3200, q: 1.6, t: 0.012, v: 0.12, delay: t })
  },
  great: () => chord([523, 659, 784], { t: 1.0, v: 0.13 }),
  perfect: () => {
    chord([523, 659, 784, 1047], { t: 1.3, v: 0.13 })
    note({ f: 1568, t: 0.9, v: 0.06, delay: 0.22, attack: 0.08, shine: 0.15 })
  },
  low: () => snap({ f: 2400, q: 2, t: 0.02, v: 0.2 }),
  hurt: () => {
    note({ f: 392, to: 262, t: 0.42, v: 0.13, attack: 0.008, shine: 0.12 })
    note({ f: 196, to: 147, t: 0.5, v: 0.08, delay: 0.04, attack: 0.02, shine: 0 })
  },
  out: () => {
    note({ f: 294, to: 147, t: 0.9, v: 0.12, attack: 0.03, shine: 0.1 })
    note({ f: 196, to: 98, t: 1.1, v: 0.09, delay: 0.05, attack: 0.05, shine: 0 })
  },
  start: () => {
    note({ f: 392, t: 0.35, v: 0.11, attack: 0.02, shine: 0.18 })
    note({ f: 587, t: 0.5, v: 0.1, delay: 0.12, attack: 0.03, shine: 0.18 })
  },
  rankup: () => {
    chord([392, 494, 587, 784], { t: 1.4, v: 0.12 })
    note({ f: 1175, t: 1.0, v: 0.06, delay: 0.3, attack: 0.08, shine: 0.2 })
  },
  coin: () => {
    note({ f: 1319, t: 0.18, v: 0.07, attack: 0.004, shine: 0.3, type: 'triangle' })
    note({ f: 1976, t: 0.3, v: 0.06, delay: 0.07, attack: 0.004, shine: 0.3, type: 'triangle' })
  },
  join: () => note({ f: 523, to: 659, t: 0.3, v: 0.07, attack: 0.02, shine: 0.15 }),
  leave: () => note({ f: 523, to: 392, t: 0.3, v: 0.06, attack: 0.02, shine: 0.1 }),
  chat: () => snap({ f: 2000, q: 2.4, t: 0.03, v: 0.22 }),
}
export type SfxName = keyof typeof SFX

/* Only the short clicks play; the tones and chimes are retired. */
const KEEP = new Set<SfxName>(['pin', 'click', 'tick', 'low', 'chat', 'lock', 'count'])

export function sfx(name: SfxName, arg?: number) {
  if (!enabled || !KEEP.has(name)) return
  try {
    if (name === 'count') SFX.count(arg)
    else (SFX[name] as () => void)()
  } catch {
    /* audio unavailable */
  }
}

/* Browsers start audio asleep until a gesture; wake it on the first one. */
export function primeAudio() {
  const wake = () => {
    ac()
    window.removeEventListener('pointerdown', wake)
    window.removeEventListener('keydown', wake)
  }
  window.addEventListener('pointerdown', wake, { once: false })
  window.addEventListener('keydown', wake, { once: false })
}
